/**
 * Feature Flags Context
 * Fetches and provides feature flags from the backend API
 */

import { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const FeatureContext = createContext(null);

// Default feature flags (used while loading or on error)
const DEFAULT_FEATURES = {
  sessionBuilder: true, // Default to true for backwards compatibility
  googleSheetsImport: true, // Default to true for backwards compatibility
  googleSso: true, // Default to true for backwards compatibility
};

/**
 * Feature Provider component
 * Fetches feature flags from /api/config on mount
 */
export function FeatureProvider({ children }) {
  const [features, setFeatures] = useState(DEFAULT_FEATURES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await api.get('/config');
        if (response.data?.success && response.data?.data?.features) {
          setFeatures(response.data.data.features);
        }
      } catch (err) {
        console.error('Failed to fetch feature config:', err);
        setError(err);
        // Keep default features on error
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, []);

  const value = {
    features,
    loading,
    error,
    // Convenience accessors
    isFeatureEnabled: (featureName) => features[featureName] ?? false,
    sessionBuilderEnabled: features.sessionBuilder,
    googleSheetsImportEnabled: features.googleSheetsImport,
    googleSsoEnabled: features.googleSso,
  };

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
}

/**
 * Hook to access feature flags
 */
export function useFeatures() {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return context;
}

export default FeatureContext;
